import React, { useState, useEffect } from 'react';
import {
  Card,
  Slider,
  Button,
  Form,
  message,
  Spin,
  Tabs,
  Space,
  Alert,
  Modal,
  Row,
  Col
} from 'antd';
import {
  SaveOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { api } from '@redux/api';
import './index.css';

const { TabPane } = Tabs;

// Weight field configuration
const WEIGHT_FIELDS = [
  {
    key: 'energy_efficiency_weight',
    label: 'Energy Efficiency',
    color: '#52c41a',
  },
  {
    key: 'equipment_condition_weight',
    label: 'Equipment Condition',
    color: '#1890ff',
  },
  {
    key: 'occupant_comfort_weight',
    label: 'Occupant Comfort',
    color: '#722ed1',
  },
  {
    key: 'service_efficiency_weight',
    label: 'Service Efficiency',
    color: '#fa8c16',
  },
  {
    key: 'technology_adoption_weight',
    label: 'Technology Adoption',
    color: '#13c2c2',
  },
  {
    key: 'cost_efficiency_weight',
    label: 'Cost Efficiency',
    color: '#eb2f96',
  },
];

const AdminSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [weights, setWeights] = useState({});
  const [totalWeight, setTotalWeight] = useState(0);

  useEffect(() => {
    loadWeightsSettings();
  }, []);

  // Calculate total weight
  useEffect(() => {
    const total = WEIGHT_FIELDS.reduce((sum, field) => {
      return sum + (weights[field.key] || 0);
    }, 0);
    setTotalWeight(total);
  }, [weights]);

  // Check if total weight is valid
  const isValidTotal = Math.abs(totalWeight - 1.0) < 0.001;

  // Load weight configuration
  const loadWeightsSettings = async () => {
    try {
      setLoading(true);
      const response = await api.getPerformanceWeights();
      
      if (response && response.status === 200) {
        const data = response.data;
        // data.weights 包含所有权重值
        setWeights(data.weights || {});
        form.setFieldsValue(data.weights || {});
      }
    } catch (error) {
      console.error('Failed to load weight configuration:', error);
      message.error('Failed to load weight configuration');
    } finally {
      setLoading(false);
    }
  };

  // Weight value change
  const handleWeightChange = (key, value) => {
    const newWeights = { ...weights, [key]: value };
    setWeights(newWeights);
    form.setFieldsValue(newWeights);
  };

  // Save configuration
  const handleSave = async () => {
    // Check if total weight equals 1.0
    if (!isValidTotal) {
      Modal.warning({
        title: 'Weight Sum Validation',
        content: (
          <div>
            <p>The total weight must equal 100% (currently {(totalWeight * 100).toFixed(0)}%).</p>
            <p>Please adjust the weights manually or click the <strong>"Auto Balance"</strong> button to automatically distribute the weights to sum to 100%.</p>
          </div>
        ),
        okText: 'Got it',
      });
      return;
    }

    // Show confirmation dialog
    Modal.confirm({
      title: 'Confirm Weight Configuration Update',
      content: (
        <div>
          <p>This action will update the performance metrics weight configuration system-wide.</p>
          <p>All facility scores will be recalculated based on the new weights.</p>
          <p style={{ marginTop: 16, fontWeight: 500 }}>Do you want to proceed?</p>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const values = await form.validateFields();

          // 构建批量更新的配置数据
          const configs = WEIGHT_FIELDS.map((field) => ({
            module: 'performance_metrics',
            param_name: field.key,
            param_value: String(values[field.key] || 0),
            description: field.label
          }));

          setLoading(true);
          const response = await api.bulkUpdateSystemConfig({ configs });
          
          if (response && response.status === 200) {
            message.success('Configuration saved successfully!');
            await loadWeightsSettings();
          }
        } catch (error) {
          console.error('Failed to save:', error);
          const errorMsg = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to save configuration';
          message.error(errorMsg);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Reset to default values
  const handleReset = () => {
    loadWeightsSettings();
  };

  // Auto normalize
  const handleNormalize = () => {
    const values = form.getFieldsValue();
    const total = WEIGHT_FIELDS.reduce((sum, field) => {
      return sum + (values[field.key] || 0);
    }, 0);

    if (total === 0) {
      message.warning('All weights cannot be 0%');
      return;
    }

    const normalized = {};
    WEIGHT_FIELDS.forEach((field) => {
      // Normalize with 0.01 step, ensure total is 1.0
      const value = (values[field.key] || 0) / total;
      normalized[field.key] = Math.round(value * 100) / 100;
    });

    // Adjust the last weight to ensure the total is exactly 1.0
    const normalizedTotal = WEIGHT_FIELDS.slice(0, -1).reduce((sum, field) => {
      return sum + normalized[field.key];
    }, 0);
    normalized[WEIGHT_FIELDS[WEIGHT_FIELDS.length - 1].key] = 
      Math.round((1.0 - normalizedTotal) * 100) / 100;

    setWeights(normalized);
    form.setFieldsValue(normalized);
    message.success('Weights automatically normalized to 100%');
  };

  return (
    <div className="admin-settings-container">
      <Spin spinning={loading}>
          <Tabs defaultActiveKey="weights">
            <TabPane
              tab={
                <span>
                  <i className="fa-solid fa-chart-pie" style={{ marginRight: 8 }}></i>
                  Performance Metrics Weight Configuration
                </span>
              }
              key="weights"
            >
              {/* Weight total alert */}
              <Alert
                message={
                  isValidTotal ? (
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      Total Weight: {(totalWeight * 100).toFixed(0)}% ✅
                    </span>
                  ) : (
                    <span>
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      Total Weight: {(totalWeight * 100).toFixed(0)}% ❌ (Must equal 100%)
                    </span>
                  )
                }
                type={isValidTotal ? 'success' : 'error'}
                showIcon={false}
                style={{ marginBottom: 24 }}
                className="weight-total-alert"
                action={
                  !isValidTotal && (
                    <Button size="small" onClick={handleNormalize}>
                      Auto Balance
                    </Button>
                  )
                }
              />

              {/* Weight configuration form */}
              <Card 
                title="Weight Settings"
                style={{ marginBottom: 24 }}
                extra={
                  <Space>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  </Space>
                }
              >
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={weights}
                >
                  <Row gutter={[24, 16]}>
                    {WEIGHT_FIELDS.map((field) => (
                      <Col key={field.key} xs={24} sm={24} md={12}>
                        <div className="weight-field-module">
                          <Form.Item
                            label={
                              <span>
                                <span style={{ color: field.color, marginRight: 8 }}>●</span>
                                {field.label}
                              </span>
                            }
                            name={field.key}
                            style={{ marginBottom: 0 }}
                          >
                            <Slider
                              min={0}
                              max={1}
                              step={0.01}
                              value={weights[field.key] || 0}
                              onChange={(value) => handleWeightChange(field.key, value)}
                              marks={{
                                0: '0%',
                                1: '100%'
                              }}
                              tooltip={{ 
                                formatter: (value) => `${(value * 100).toFixed(0)}%`
                              }}
                            />
                          </Form.Item>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Form>
              </Card>
            </TabPane>

            {/* Other settings tab */}
            {/* <TabPane
              tab={
                <span>
                  <i className="fa-solid fa-cog" style={{ marginRight: 8 }}></i>
                  Other Settings
                </span>
              }
              key="other"
            >
              <Card>
                <p>Other admin settings features are under development...</p>
              </Card>
            </TabPane> */}
          </Tabs>
      </Spin>
    </div>
  );
};

export default AdminSettings;